import { useCallback, useEffect, useRef, useState } from 'react';
import { Download, Minus, Plus, RotateCcw, X } from 'lucide-react';

export default function CanvasMediaPreview({ media, onClose, onDownload }) {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);
  const isImage = media?.kind === 'image';

  const reset = useCallback(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
      if (isImage && (event.key === '+' || event.key === '=')) setScale((value) => Math.min(6, value + 0.2));
      if (isImage && event.key === '-') setScale((value) => Math.max(0.25, value - 0.2));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isImage, onClose]);

  if (!media?.url) return null;
  const zoom = (delta) => setScale((value) => Math.max(0.25, Math.min(6, Number((value + delta).toFixed(2)))));

  return <div className="canvas-media-viewer" role="dialog" aria-modal="true" aria-label={`${media.name || '素材'}预览`}>
    <header className="canvas-media-viewer__header">
      <div><strong>{media.name || (isImage ? '图片预览' : '视频预览')}</strong><span>{isImage ? '图片' : '视频'}</span></div>
      <div>
        <button type="button" onClick={onDownload}><Download size={17} /><span>下载</span></button>
        <button type="button" className="is-icon" onClick={onClose} aria-label="关闭预览"><X size={20} /></button>
      </div>
    </header>
    <div
      className={`canvas-media-viewer__stage ${isImage && scale > 1 ? 'is-zoomed' : ''}`}
      onWheel={isImage ? (event) => { event.preventDefault(); zoom(event.deltaY > 0 ? -0.15 : 0.15); } : undefined}
      onPointerDown={isImage ? (event) => {
        if (scale <= 1) return;
        event.currentTarget.setPointerCapture(event.pointerId);
        dragRef.current = { x: event.clientX, y: event.clientY, startX: position.x, startY: position.y };
      } : undefined}
      onPointerMove={isImage ? (event) => {
        if (!dragRef.current) return;
        setPosition({
          x: dragRef.current.startX + event.clientX - dragRef.current.x,
          y: dragRef.current.startY + event.clientY - dragRef.current.y,
        });
      } : undefined}
      onPointerUp={() => { dragRef.current = null; }}
      onPointerCancel={() => { dragRef.current = null; }}
      onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}
    >
      {isImage
        ? <img src={media.url} alt={media.name || '图片预览'} draggable="false" onClick={onClose} style={{ transform: `translate3d(${position.x}px, ${position.y}px, 0) scale(${scale})` }} />
        : <video src={media.url} poster={media.poster || undefined} controls autoPlay playsInline />}
    </div>
    {isImage && <div className="canvas-media-viewer__controls">
      <button type="button" onClick={() => zoom(-0.2)} aria-label="缩小"><Minus size={17} /></button>
      <span>{Math.round(scale * 100)}%</span>
      <button type="button" onClick={() => zoom(0.2)} aria-label="放大"><Plus size={17} /></button>
      <i />
      <button type="button" onClick={reset} aria-label="重置缩放"><RotateCcw size={16} /></button>
    </div>}
  </div>;
}
